import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {String} sheetName - Name of the sheet
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const ws = XLSX.utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  
  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Create blob and download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Format bills data for Excel export
 */
export const formatBillsForExport = (bills) => {
  return bills.map(bill => ({
    'Date': bill.date,
    'Branch': bill.branch_name,
    'Theater': bill.theater_name,
    'Time Slot': bill.time_slot,
    'Number of People': bill.num_people,
    'Theater Price': bill.total_theater_price,
    'Items Total': bill.items.reduce((sum, item) => sum + item.total, 0),
    'Total Bill Amount': bill.total_bill_amount,
    'Discount Amount': bill.discount_amount,
    'Discount Reason': bill.discount_reason || 'N/A',
    'Actual Amount': bill.is_cancelled ? 0 : bill.actual_amount,
    'Advance Paid': bill.advance_paid || 0,
    'Balance Due': bill.is_cancelled ? 0 : (bill.actual_amount - (bill.advance_paid || 0)),
    'Payment Mode': bill.payment_mode,
    'Cash Amount': bill.cash_amount || 0,
    'Online Amount': bill.online_amount || 0,
    'Scanner': bill.scanner_name || 'N/A',
    'Status': bill.is_cancelled ? 'CANCELLED' : 'Active',
    'Cancellation Reason': bill.is_cancelled ? (bill.cancellation_reason || 'N/A') : 'N/A',
    'Customer Name': bill.customer_name || 'N/A',
    'Customer Phone': bill.customer_phone || 'N/A',
    'Created By': bill.created_by_username,
    'Created At': new Date(bill.created_at).toLocaleString()
  }));
};

/**
 * Format expenses data for Excel export
 */
export const formatExpensesForExport = (expenses) => {
  return expenses.map(expense => ({
    'Date': expense.date,
    'Branch': expense.branch_name || 'N/A',
    'Category': expense.category,
    'Description': expense.description,
    'Amount': expense.amount,
    'Payment Mode': expense.payment_mode,
    'Created By': expense.created_by_username || 'Admin',
    'Created At': new Date(expense.created_at).toLocaleString()
  }));
};

/**
 * Format cash handovers for Excel export
 */
export const formatCashHandoversForExport = (handovers) => {
  return handovers.map(handover => ({
    'Date': handover.date,
    'Branch': handover.branch_name,
    'Amount': handover.amount,
    'Handed To': handover.handed_to,
    'Balance Before': handover.balance_before,
    'Balance After': handover.balance_after,
    'Created By': handover.created_by_username,
    'Created At': new Date(handover.created_at).toLocaleString()
  }));
};

/**
 * Format master data for Excel export
 */
export const formatMasterDataForExport = (data, type) => {
  switch(type) {
    case 'branches':
      return data.map(item => ({
        'Name': item.name,
        'Location': item.location
      }));
    
    case 'theaters':
      return data.map(item => ({
        'Name': item.name,
        'Branch': item.branch_name,
        'Min Capacity': item.min_capacity,
        'Base Price': item.price,
        'Extra Person Charge': item.extra_person_charge
      }));
    
    case 'cakes':
    case 'decorations':
    case 'food':
      return data.map(item => ({
        'Name': item.name,
        'Branch': item.branch_name,
        'Price': item.price
      }));
    
    case 'slots':
      return data.map(item => ({
        'Theater': item.theater_name,
        'Time Slot': item.time_slot
      }));
    
    case 'scanners':
      return data.map(item => ({
        'Scanner Name': item.name
      }));
    
    default:
      return data;
  }
};

/**
 * Format users for Excel export
 */
export const formatUsersForExport = (users) => {
  return users.map(user => ({
    'Username': user.username,
    'Role': user.role,
    'Branch': user.branch_name || 'N/A',
    'Created At': new Date(user.created_at).toLocaleString()
  }));
};

/**
 * Export GST Bills to Excel with multiple sheets
 * @param {Array} gstBills - Array of GST bill objects
 * @param {String} filename - Name of the file
 */
export const exportGSTBillsToExcel = (gstBills, filename = 'GST_Bills_Export') => {
  if (!gstBills || gstBills.length === 0) {
    throw new Error('No GST bills to export');
  }

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Sheet 1: GST Bills Summary
  const summaryData = gstBills.map(bill => ({
    'GST Bill Number': bill.gst_bill_number,
    'Bill Date': bill.bill_date,
    'Branch': bill.branch_name,
    'Branch State': bill.branch_state,
    'Branch GSTIN': bill.branch_gstin,
    'Customer Name': bill.customer_name,
    'Customer Phone': bill.customer_phone,
    'Customer Email': bill.customer_email || 'N/A',
    'Billed By': bill.billed_by || 'N/A',
    'Customer State': bill.customer_state || 'N/A',
    'Customer GSTIN': bill.customer_gstin || 'N/A',
    'Bill Type': bill.is_interstate ? 'Interstate (IGST)' : 'Intrastate (CGST+SGST)',
    'Total Taxable Amount': bill.total_taxable_amount,
    'Total CGST': bill.total_cgst || 0,
    'Total SGST': bill.total_sgst || 0,
    'Total IGST': bill.total_igst || 0,
    'Total Tax': bill.total_tax_amount,
    'Grand Total': bill.grand_total,
    'Regular Bills Included': bill.regular_bill_ids.length,
    'Generated By': bill.generated_by_username,
    'Generated At': new Date(bill.generated_at).toLocaleString(),
    'Notes': bill.notes || 'N/A'
  }));

  const summaryWs = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summaryWs, 'GST Bills Summary');

  // Sheet 2: Itemized Details
  const itemizedData = [];
  gstBills.forEach(bill => {
    bill.items.forEach(item => {
      itemizedData.push({
        'GST Bill Number': bill.gst_bill_number,
        'Bill Date': bill.bill_date,
        'Branch': bill.branch_name,
        'Customer Name': bill.customer_name,
        'Billed By': bill.billed_by || 'N/A',
        'Item Type': item.item_type,
        'Item Name': item.item_name,
        'Quantity': item.quantity,
        'Rate': item.rate,
        'Taxable Amount': item.taxable_amount,
        'GST Rate (%)': item.gst_rate,
        'CGST': item.cgst_amount || 0,
        'SGST': item.sgst_amount || 0,
        'IGST': item.igst_amount || 0,
        'Total Amount': item.total_amount
      });
    });
  });

  const itemizedWs = XLSX.utils.json_to_sheet(itemizedData);
  XLSX.utils.book_append_sheet(wb, itemizedWs, 'Itemized Details');

  // Sheet 3: Tax Summary (Branch-wise and Period-wise)
  const taxSummary = {};
  gstBills.forEach(bill => {
    const key = `${bill.branch_name}_${bill.bill_date.substring(0, 7)}`; // Group by branch and month
    if (!taxSummary[key]) {
      taxSummary[key] = {
        'Branch': bill.branch_name,
        'Period (YYYY-MM)': bill.bill_date.substring(0, 7),
        'Number of Bills': 0,
        'Total Taxable Amount': 0,
        'Total CGST': 0,
        'Total SGST': 0,
        'Total IGST': 0,
        'Total Tax': 0,
        'Grand Total': 0
      };
    }
    taxSummary[key]['Number of Bills']++;
    taxSummary[key]['Total Taxable Amount'] += bill.total_taxable_amount;
    taxSummary[key]['Total CGST'] += bill.total_cgst || 0;
    taxSummary[key]['Total SGST'] += bill.total_sgst || 0;
    taxSummary[key]['Total IGST'] += bill.total_igst || 0;
    taxSummary[key]['Total Tax'] += bill.total_tax_amount;
    taxSummary[key]['Grand Total'] += bill.grand_total;
  });

  const taxSummaryData = Object.values(taxSummary);
  const taxSummaryWs = XLSX.utils.json_to_sheet(taxSummaryData);
  XLSX.utils.book_append_sheet(wb, taxSummaryWs, 'Tax Summary');

  // Sheet 4: Customer-wise Summary (for B2B analysis)
  const customerSummary = {};
  gstBills.forEach(bill => {
    const key = bill.customer_gstin || bill.customer_phone; // Group by GSTIN or phone
    if (!customerSummary[key]) {
      customerSummary[key] = {
        'Customer Name': bill.customer_name,
        'Customer Phone': bill.customer_phone,
        'Customer GSTIN': bill.customer_gstin || 'B2C (No GSTIN)',
        'Customer State': bill.customer_state || 'N/A',
        'Number of Bills': 0,
        'Total Amount': 0,
        'Total Tax': 0
      };
    }
    customerSummary[key]['Number of Bills']++;
    customerSummary[key]['Total Amount'] += bill.grand_total;
    customerSummary[key]['Total Tax'] += bill.total_tax_amount;
  });

  const customerSummaryData = Object.values(customerSummary);
  const customerSummaryWs = XLSX.utils.json_to_sheet(customerSummaryData);
  XLSX.utils.book_append_sheet(wb, customerSummaryWs, 'Customer Summary');

  // Sheet 5: GST Rate-wise Summary
  const gstRateSummary = {};
  gstBills.forEach(bill => {
    bill.items.forEach(item => {
      const key = `${item.gst_rate}%`;
      if (!gstRateSummary[key]) {
        gstRateSummary[key] = {
          'GST Rate': key,
          'Total Taxable Amount': 0,
          'Total Tax': 0,
          'Number of Items': 0
        };
      }
      gstRateSummary[key]['Total Taxable Amount'] += item.taxable_amount;
      const itemTax = (item.cgst_amount || 0) + (item.sgst_amount || 0) + (item.igst_amount || 0);
      gstRateSummary[key]['Total Tax'] += itemTax;
      gstRateSummary[key]['Number of Items']++;
    });
  });

  const gstRateSummaryData = Object.values(gstRateSummary);
  const gstRateSummaryWs = XLSX.utils.json_to_sheet(gstRateSummaryData);
  XLSX.utils.book_append_sheet(wb, gstRateSummaryWs, 'GST Rate Summary');

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Create blob and download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Export branch-wise business summary to Excel
 */
export const exportBranchWiseBusinessSummary = (branchData, dateRange) => {
  const formattedData = branchData.map(branch => ({
    'Branch Name': branch.branch_name,
    'Total Advance': `₹${branch.total_advance.toFixed(2)}`,
    'Cash Collected': `₹${branch.total_cash.toFixed(2)}`,
    'Online Payments': `₹${branch.total_online.toFixed(2)}`,
    'Total Revenue': `₹${branch.total_revenue.toFixed(2)}`,
    'Total Bills': branch.total_bills
  }));

  // Add summary row
  const totalAdvance = branchData.reduce((sum, b) => sum + b.total_advance, 0);
  const totalCash = branchData.reduce((sum, b) => sum + b.total_cash, 0);
  const totalOnline = branchData.reduce((sum, b) => sum + b.total_online, 0);
  const totalRevenue = branchData.reduce((sum, b) => sum + b.total_revenue, 0);
  const totalBills = branchData.reduce((sum, b) => sum + b.total_bills, 0);

  formattedData.push({
    'Branch Name': 'TOTAL',
    'Total Advance': `₹${totalAdvance.toFixed(2)}`,
    'Cash Collected': `₹${totalCash.toFixed(2)}`,
    'Online Payments': `₹${totalOnline.toFixed(2)}`,
    'Total Revenue': `₹${totalRevenue.toFixed(2)}`,
    'Total Bills': totalBills
  });

  const ws = XLSX.utils.json_to_sheet(formattedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Branch-wise Summary');

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  const filename = dateRange 
    ? `Business_Summary_BranchWise_${dateRange}_${new Date().toISOString().split('T')[0]}`
    : `Business_Summary_BranchWise_${new Date().toISOString().split('T')[0]}`;
  
  saveAs(blob, `${filename}.xlsx`);
};
